import { useEffect } from 'react';

/**
 * Custom React hook that listens for the Escape key press on a specified event target.
 * When the Escape key is pressed, it calls the provided `onEscape` callback and optionally restores focus to a given element.
 *
 * @param onEscape - Callback function to execute when the Escape key is pressed.
 * @param enabled - Whether the Escape key listener is active. Defaults to `true`.
 * @param eventTarget - The target to listen for keydown events (e.g., `document`, `window`, or an HTML element).
 * @param restoreFocusTarget - Optional element or query selector string to restore focus to after Escape is pressed.
 */
export function useEscapeKey(
  onEscape: () => void,
  enabled = true,
  eventTarget: Document | Window | HTMLElement,
  restoreFocusTarget?: HTMLElement | string,
) {
  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (event: Event) => {
      if (event instanceof KeyboardEvent && event.key === 'Escape') {
        onEscape();

        if (restoreFocusTarget) {
          const focusElement =
            typeof restoreFocusTarget === 'string'
              ? document.querySelector(restoreFocusTarget)
              : restoreFocusTarget;

          // Added setTimeout here to execute focus only after all DOM events have finished (ex: closing of modals)
          setTimeout(() => {
            if (focusElement instanceof HTMLElement) {
              focusElement?.focus();
            }
          });
        }
      }
    };

    eventTarget.addEventListener('keydown', handleKeyDown);
    return () => {
      eventTarget.removeEventListener('keydown', handleKeyDown);
    };
  }, [onEscape, enabled, eventTarget, restoreFocusTarget]);
}
